import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../components/api/axiosInstance";
import { FaArrowLeft, FaChevronDown } from "react-icons/fa";
import { unwrapString, formatPascal } from "../utils/formatters";
import "../assets/css/EditLead.css";
import "../assets/css/AddLead.css";
import "../assets/css/Issues.css";
import "../assets/css/Permissions.css";
import { getAuthItem } from "../utils/authStorage";
import { formValueWithMobileSanitize, mobileValidationMessage } from "../utils/phone";

function safeString(v) {
  return v == null ? "" : String(v);
}

function getCreatedBy() {
  try {
    const userStr = getAuthItem("user");
    if (!userStr) return 1;
    const user = JSON.parse(userStr);
    return user.user_id || user.id || 1;
  } catch {
    return 1;
  }
}

function getId(v) {
  if (v == null || v === "") return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

function parseDecimal(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim().replace(/,/g, ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

function EditJobLead({ leadId, onBack, onUpdateLead }) {
  const createdBy = getCreatedBy();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    altPhone: "",
    qualificationId: "",
    experienceYears: "",
    currentCompany: "",
    designationId: "",
    expectedSalary: "",
    currentSalary: "",
    skills: "",
    preferredLocation: "",
    sourceId: "",
    statusId: "",
    assignedTo: "",
    addressLine1: "",
    addressLine2: "",
    cityId: "",
    stateId: "",
    countryId: "",
    zipCode: "",
  });

  const [phoneCountryCode, setPhoneCountryCode] = useState("+91");
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeUrl, setResumeUrl] = useState("");

  const [followupData, setFollowupData] = useState({
    followupDate: "",
    followupType: "",
    nextFollowupDate: "",
    remarks: "",
  });
  const [followupId, setFollowupId] = useState(null);

  const [roles, setRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [showRolesDropdown, setShowRolesDropdown] = useState(false);

  const [qualifications, setQualifications] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const phoneCountryOptions = useMemo(() => {
    return (countries || []).map((c) => ({
      id: c.country_id ?? c.Country_Id ?? c.id,
      code: (c.country_code || c.country_short_code || "").trim(),
      name: c.country_name || c.Country_Name || c.name || "",
    }));
  }, [countries]);

  const toggleRole = (roleId) => {
    setSelectedRoleIds((prev) => {
      const next = prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId];
      return next;
    });
  };

  const allRolesSelected = roles.length > 0 && selectedRoleIds.length === roles.length;

  const handleToggleSelectAllRoles = () => {
    setSelectedRoleIds((prev) => {
      const selectAllIds = roles.map((r) => parseInt(r.id, 10)).filter((id) => !Number.isNaN(id));

      if (selectAllIds.length === 0) return [];

      const isAlreadyAllSelected = selectAllIds.every((id) => prev.includes(id));
      return isAlreadyAllSelected ? [] : selectAllIds;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: formValueWithMobileSanitize(name, value) }));
  };

  const handleFollowupChange = (e) => {
    const { name, value } = e.target;
    setFollowupData((p) => ({ ...p, [name]: value }));
  };

  // Load dropdown lists + lead details

  // Fetch active DigiX staff users for Assigned To
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await axiosInstance.get("/users", { params: { status: "Active" } });
        const list = Array.isArray(response.data) ? response.data : [];
        const mapped = list
          .map((u) => ({
            id: u.user_id ?? u.User_Id ?? u.id,
            name: [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.email || `User #${u.user_id}`,
          }))
          .filter((u) => u.id != null)
          .sort((a, b) => a.name.localeCompare(b.name));
        setUsers(mapped);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [
          rolesRes,
          qualRes,
          desRes,
          sourceRes,
          statusRes,
          countriesRes,
          leadRes,
        ] = await Promise.all([
          axiosInstance.get("/roles"),
          axiosInstance.get("/qualifications"),
          axiosInstance.get("/designations"),
          axiosInstance.get("/lead-sources"),
          axiosInstance.get("/lead-statuses"),
          axiosInstance.get("/countries"),
          axiosInstance.get(`/job-lead/${leadId}`),
        ]);

        const rawRoles = Array.isArray(rolesRes.data)
          ? rolesRes.data
          : Array.isArray(rolesRes.data?.data)
            ? rolesRes.data.data
            : [];
        setRoles(
          rawRoles
            .map((r) => ({
              id: r.role_id ?? r.Role_Id ?? r.id,
              name: r.role_name ?? r.Role_Name ?? r.name ?? "",
            }))
            .filter((r) => r.id != null && String(r.name).trim() !== "")
        );

        const rawQuals = Array.isArray(qualRes.data)
          ? qualRes.data
          : Array.isArray(qualRes.data?.data)
            ? qualRes.data.data
            : [];
        setQualifications(
          rawQuals
            .map((q) => ({
              id: q.Qualification_Id ?? q.qualification_id ?? q.id,
              name: q.Qualification_Name ?? q.qualification_name ?? q.name ?? "",
            }))
            .filter((q) => q.id != null && String(q.name).trim() !== "")
        );

        const rawDesignations = Array.isArray(desRes.data)
          ? desRes.data
          : Array.isArray(desRes.data?.data)
            ? desRes.data.data
            : [];
        setDesignations(
          rawDesignations
            .map((d) => ({
              id: d.Designation_Id ?? d.designation_id ?? d.id,
              name: d.Designation_Name ?? d.designation_name ?? d.name ?? "",
            }))
            .filter((d) => d.id != null && String(d.name).trim() !== "")
        );

        const rawSources = Array.isArray(sourceRes.data)
          ? sourceRes.data
          : Array.isArray(sourceRes.data?.data)
            ? sourceRes.data.data
            : [];
        setSources(
          rawSources
            .map((s) => ({
              id: s.Source_Id ?? s.source_id ?? s.id,
              name: s.Source_Name ?? s.source_name ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "")
        );

        const rawStatuses = Array.isArray(statusRes.data)
          ? statusRes.data
          : Array.isArray(statusRes.data?.data)
            ? statusRes.data.data
            : [];
        setStatuses(
          rawStatuses
            .map((s) => ({
              id: s.Status_Id ?? s.status_id ?? s.id,
              name: s.Status ?? s.status ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "")
        );

        const rawCountries = Array.isArray(countriesRes.data)
          ? countriesRes.data
          : Array.isArray(countriesRes.data?.data)
            ? countriesRes.data.data
            : [];
        setCountries(rawCountries);

        const jobLead =
          leadRes.data?.job_lead ||
          leadRes.data?.jobLead ||
          leadRes.data?.job_lead_data ||
          leadRes.data;

        const address = jobLead?.address || leadRes.data?.address || {};

        const roleIds =
          Array.isArray(jobLead?.Roles) ? jobLead.Roles.map((r) => r.Role_Id ?? r.role_id) : [];

        const countryCode = safeString(unwrapString(jobLead?.Country_Code) || "+91");

        setPhoneCountryCode(countryCode);
        setSelectedRoleIds(roleIds.filter((x) => x != null).map((x) => parseInt(x, 10)).filter((x) => !Number.isNaN(x)));

        // Set country first; then load states/cities for better UX
        setFormData({
          firstName: safeString(unwrapString(jobLead?.First_Name)),
          lastName: safeString(unwrapString(jobLead?.Last_Name)),
          email: safeString(unwrapString(jobLead?.Email)),
          phone: safeString(unwrapString(jobLead?.Phone)),
          altPhone: safeString(unwrapString(jobLead?.Alt_Phone)),
          qualificationId: jobLead?.Qualification_Id ?? jobLead?.qualification_id ?? "",
          experienceYears: safeString(jobLead?.Experience_Years),
          currentCompany: safeString(unwrapString(jobLead?.Current_Company)),
          designationId: jobLead?.Designation_Id ?? jobLead?.designation_id ?? "",
          expectedSalary: safeString(unwrapString(jobLead?.Expected_Salary)),
          currentSalary: safeString(unwrapString(jobLead?.Current_Salary)),
          skills: safeString(unwrapString(jobLead?.Skills)),
          preferredLocation: safeString(unwrapString(jobLead?.Preferred_Location)),
          sourceId: jobLead?.Source_Id ?? jobLead?.source_id ?? "",
          statusId: jobLead?.Status_Id ?? jobLead?.status_id ?? "",
          assignedTo: (jobLead?.Assigned_To ?? jobLead?.assigned_to) != null && (jobLead?.Assigned_To ?? jobLead?.assigned_to) !== "" ? String(jobLead?.Assigned_To ?? jobLead?.assigned_to) : "",
          addressLine1: safeString(unwrapString(address?.Address_Line_1 ?? jobLead?.Address_Line_1)),
          addressLine2: safeString(unwrapString(address?.Address_Line_2 ?? jobLead?.Address_Line_2)),
          cityId: address?.City_Id ?? jobLead?.City_Id ?? "",
          stateId: address?.State_Id ?? jobLead?.State_Id ?? "",
          countryId: address?.Country_Id ?? jobLead?.Country_Id ?? "",
          zipCode: safeString(unwrapString(address?.Zip_Code ?? jobLead?.Zip_Code)),
        });

        setResumeUrl(jobLead?.Resume_Url || jobLead?.Resume_Path || "");

        // Fetch states/cities to ensure selects are populated
        const countryId = getId(address?.Country_Id ?? jobLead?.Country_Id);
        if (countryId) {
          setLoadingStates(true);
          try {
            const statesRes = await axiosInstance.get(`/states/${countryId}`);
            const raw = statesRes.data;
            const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
            setStates(arr);
          } catch {
            setStates([]);
          } finally {
            setLoadingStates(false);
          }
        }

        const stateId = getId(address?.State_Id ?? jobLead?.State_Id);
        if (stateId) {
          setLoadingCities(true);
          try {
            const citiesRes = await axiosInstance.get(`/cities/${stateId}`);
            const raw = citiesRes.data;
            const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
            setCities(arr);
          } catch {
            setCities([]);
          } finally {
            setLoadingCities(false);
          }
        }

        // Fetch latest followup for this job lead
        try {
          const followupRes = await axiosInstance.get("/job-leads-followups");
          const raw = followupRes?.data;
          const arr = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.job_leads_followups)
              ? raw.job_leads_followups
              : Array.isArray(raw?.data)
                ? raw.data
                : [];
          const forLead = arr.filter((f) => {
            const jid = f.Job_Lead_Id ?? f.job_lead_id ?? f.JobLead_Id ?? f.jobLead_id ?? null;
            return jid != null && String(jid) === String(leadId);
          });
          if (forLead.length > 0) {
            const sorted = forLead.sort((a, b) => {
              const da = new Date(a.Followup_Date || a.followup_date || 0);
              const db = new Date(b.Followup_Date || b.followup_date || 0);
              return db - da;
            });
            const latest = sorted[0];
            const formatDateTimeLocal = (dateStr) => {
              if (!dateStr) return "";
              try {
                const d = new Date(dateStr);
                if (isNaN(d.getTime())) return "";
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                const h = String(d.getHours()).padStart(2, "0");
                const min = String(d.getMinutes()).padStart(2, "0");
                return `${y}-${m}-${day}T${h}:${min}`;
              } catch {
                return "";
              }
            };
            setFollowupData({
              followupDate: formatDateTimeLocal(latest.Followup_Date || latest.followup_date),
              followupType: latest.Followup_Type || latest.followup_type || "",
              nextFollowupDate: formatDateTimeLocal(latest.Next_Followup_Date || latest.next_followup_date),
              remarks: latest.Remarks || latest.remarks || "",
            });
            setFollowupId(latest.Followup_Id ?? latest.followup_id ?? latest.id);
          }
        } catch (followupErr) {
          console.error("Error fetching job lead followups:", followupErr);
        }
      } catch (err) {
        console.error("Error loading job lead:", err);
        alert("Failed to load job lead details.");
      } finally {
        setLoading(false);
      }
    };

    if (leadId) load();
  }, [leadId]);

  const loadStatesForCountry = async (countryId) => {
    if (!countryId) {
      setStates([]);
      setCities([]);
      return;
    }
    setLoadingStates(true);
    try {
      const statesRes = await axiosInstance.get(`/states/${countryId}`);
      const raw = statesRes.data;
      const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setStates(arr);
    } catch {
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const loadCitiesForState = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    try {
      const citiesRes = await axiosInstance.get(`/cities/${stateId}`);
      const raw = citiesRes.data;
      const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setCities(arr);
    } catch {
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  // If user changes country/state manually, reload dependent dropdowns
  useEffect(() => {
    const countryId = getId(formData.countryId);
    if (!countryId) return;
    loadStatesForCountry(countryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.countryId]);

  useEffect(() => {
    const stateId = getId(formData.stateId);
    if (!stateId) return;
    loadCitiesForState(stateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.stateId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phoneError = mobileValidationMessage(formData.phone, { required: true, label: "Phone" });
    if (phoneError) {
      alert(phoneError);
      return;
    }
    const altError = mobileValidationMessage(formData.altPhone, { label: "Alternate phone" });
    if (altError) {
      alert(altError);
      return;
    }
    setSubmitting(true);
    try {
      const updatedBy = getCreatedBy();

      const payload = new FormData();
      payload.append("First_Name", formatPascal(formData.firstName) || "");
      payload.append("Last_Name", formatPascal(formData.lastName) || "");
      payload.append("Email", formData.email);
      payload.append("Country_Code", safeString(phoneCountryCode));
      payload.append("Phone", safeString(formData.phone));
      payload.append("Alt_Phone", formData.altPhone || "");

      payload.append("Qualification_Id", String(getId(formData.qualificationId) ?? ""));
      const expYears = parseDecimal(formData.experienceYears);
      payload.append("Experience_Years", expYears != null ? String(expYears) : "");
      payload.append("Current_Company", formData.currentCompany || "");
      payload.append("Designation_Id", String(getId(formData.designationId) ?? ""));
      const expectedSal = parseDecimal(formData.expectedSalary);
      payload.append("Expected_Salary", expectedSal != null ? String(expectedSal) : formData.expectedSalary?.trim() || "");
      const currentSal = parseDecimal(formData.currentSalary);
      if (currentSal != null) {
        payload.append("Current_Salary", String(currentSal));
      }
      payload.append("Skills", formData.skills || "");
      // Backend field name is inconsistent in some environments, so send both.
      payload.append("Preferred_Location", formData.preferredLocation || "");
      payload.append("Prefered_Location", formData.preferredLocation || "");

      payload.append("Source_Id", String(getId(formData.sourceId) ?? ""));
      payload.append("Status_Id", String(getId(formData.statusId) ?? ""));
      payload.append("Assigned_To", String(getId(formData.assignedTo) ?? ""));

      payload.append("Address_Line_1", formData.addressLine1 || "");
      payload.append("Address_Line_2", formData.addressLine2 || "");
      payload.append("City_Id", String(getId(formData.cityId) ?? ""));
      payload.append("State_Id", String(getId(formData.stateId) ?? ""));
      payload.append("Country_Id", String(getId(formData.countryId) ?? ""));
      payload.append("Zip_Code", formData.zipCode || "");

      payload.append("Role_Ids", JSON.stringify(selectedRoleIds));

      if (resumeFile) {
        payload.append("Resume", resumeFile);
      }
      // Backend keeps existing `Created_By` on update; your sample request doesn't send it.
      payload.append("Updated_By", String(updatedBy));

      const response = await axiosInstance.put(`/update-job-lead/${leadId}`, payload);

      // Create or update job followup if followup data is provided
      if (followupData.followupDate) {
        try {
          const formatDateTime = (dateTimeString) => {
            if (!dateTimeString) return null;
            const withSeconds = /:\d{2}:\d{2}$/.test(dateTimeString) ? dateTimeString : `${dateTimeString}:00`;
            return withSeconds;
          };
          const updatedBy = getCreatedBy();
          if (followupId) {
            await axiosInstance.put(`/update-job-followup/${followupId}`, {
              Followup_Date: formatDateTime(followupData.followupDate),
              Followup_Type: followupData.followupType || null,
              Job_Lead_Id: leadId,
              Remarks: followupData.remarks || null,
              Next_Followup_Date: formatDateTime(followupData.nextFollowupDate) || null,
              Updated_By: updatedBy,
            });
          } else {
            await axiosInstance.post("/create-job-followup", {
              Created_By: updatedBy,
              Followup_Date: formatDateTime(followupData.followupDate),
              Followup_Type: followupData.followupType || null,
              Job_Lead_Id: leadId,
              Next_Followup_Date: formatDateTime(followupData.nextFollowupDate) || null,
              Remarks: followupData.remarks || null,
            });
          }
        } catch (followupErr) {
          console.error("Error creating/updating job followup:", followupErr);
          // Don't fail the whole update
        }
      }

      const msg =
        response.data?.message ||
        response.data?.error ||
        "Job lead updated successfully.";
      alert(msg);

      if (onUpdateLead) onUpdateLead();
      if (onBack) onBack();
    } catch (err) {
      console.error("Error updating job lead:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to update job lead.";
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="editlead-container job-lead-page">
        <div className="editlead-loading">Loading job lead details...</div>
      </div>
    );
  }

  return (
    <div className="editlead-container job-lead-page">
      <button type="button" className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back
      </button>

      <div className="issues-main-card">
      <form className="editlead-form" onSubmit={handleSubmit}>
        {/* Contact Details */}
        <div className="form-section">
          <h2 className="section-title">Contact Details</h2>
          <div className="form-grid job-lead-main-grid">
            <div className="form-group">
              <label>
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>
                Phone <span className="required">*</span>
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                  style={{ width: 80, flexShrink: 0 }}
                >
                  <option value="+91">+91</option>
                  {phoneCountryOptions
                    .filter((x) => x.code)
                    .map((c) => (
                      <option key={c.id ?? c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                </select>
                <input style={{ flex: 1 }} type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-group">
              <label>Alt Phone</label>
              <input type="tel" name="altPhone" value={formData.altPhone} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>
                Source <span className="required">*</span>
              </label>
              <select name="sourceId" value={formData.sourceId} onChange={handleChange} required>
                <option value="">Select</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="statusId" value={formData.statusId} onChange={handleChange}>
                <option value="">Select</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Assigned To</label>
              <select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
                disabled={loadingUsers}
              >
                {loadingUsers && <option value="">Loading users...</option>}
                {!loadingUsers && <option value="">Select user</option>}
                {users.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Role & Experience */}
        <div className="form-section">
          <h2 className="section-title">Role & Experience</h2>
          <div className="form-grid job-lead-main-grid">
            <div className="form-group">
              <label>Qualification</label>
              <select name="qualificationId" value={formData.qualificationId} onChange={handleChange}>
                <option value="">Select</option>
                {qualifications.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Experience Years</label>
              <input
                type="text"
                name="experienceYears"
                value={formData.experienceYears}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Current Company</label>
              <input type="text" name="currentCompany" value={formData.currentCompany} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Designation</label>
              <select name="designationId" value={formData.designationId} onChange={handleChange}>
                <option value="">Select</option>
                {designations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Skills</label>
              <input type="text" name="skills" value={formData.skills} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Preferred Location</label>
              <input
                type="text"
                name="preferredLocation"
                value={formData.preferredLocation}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Compensation */}
        <div className="form-section">
          <h2 className="section-title">Compensation</h2>
          <div className="form-grid job-lead-main-grid">
            <div className="form-group">
              <label>Expected Salary</label>
              <input type="text" name="expectedSalary" value={formData.expectedSalary} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Current Salary</label>
              <input type="text" name="currentSalary" value={formData.currentSalary} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="form-section">
          <h2 className="section-title">Address</h2>
          <div className="form-grid job-lead-main-grid">
            <div className="form-group">
              <label>Address Line 1</label>
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Address Line 2</label>
              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Country</label>
              <select
                name="countryId"
                value={formData.countryId}
                onChange={(e) => setFormData((p) => ({ ...p, countryId: e.target.value, stateId: "", cityId: "" }))}
                disabled={loadingCountries}
              >
                <option value="">Select</option>
                {countries.map((c) => (
                  <option key={c.country_id ?? c.Country_Id ?? c.id} value={c.country_id ?? c.Country_Id ?? c.id}>
                    {c.country_name || c.Country_Name || c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>State</label>
              <select
                name="stateId"
                value={formData.stateId}
                onChange={(e) => setFormData((p) => ({ ...p, stateId: e.target.value, cityId: "" }))}
                disabled={loadingStates}
              >
                <option value="">{loadingStates ? "Loading..." : "Select"}</option>
                {states.map((s) => (
                  <option key={s.state_id ?? s.State_Id ?? s.id} value={s.state_id ?? s.State_Id ?? s.id}>
                    {s.state_name || s.State_Name || s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>City</label>
              <select
                name="cityId"
                value={formData.cityId}
                onChange={(e) => setFormData((p) => ({ ...p, cityId: e.target.value }))}
                disabled={loadingCities}
              >
                <option value="">{loadingCities ? "Loading..." : "Select"}</option>
                {cities.map((c) => (
                  <option key={c.city_id ?? c.City_Id ?? c.id} value={c.city_id ?? c.City_Id ?? c.id}>
                    {c.city_name || c.City_Name || c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Zip Code</label>
              <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Roles */}
        <div className="form-section">
          <h2 className="section-title">Roles</h2>
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Role</label>
              <div style={{ width: "100%", position: "relative" }}>
                <div
                  className="permissions-select-trigger"
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowRolesDropdown((o) => !o)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowRolesDropdown((o) => !o);
                    }
                  }}
                >
                  <span className="permissions-select-text">
                    {roles.length === 0
                      ? "Loading roles..."
                      : selectedRoleIds.length === 0
                        ? "Select Role"
                        : `${selectedRoleIds.length} role${
                            selectedRoleIds.length > 1 ? "s" : ""
                          } selected`}
                  </span>
                  <FaChevronDown
                    className={`permissions-select-chevron ${showRolesDropdown ? "open" : ""}`}
                  />
                </div>

                {showRolesDropdown && roles.length > 0 && (
                  <div
                    className="permissions-select-panel"
                    style={{ maxHeight: 220, overflowY: "auto" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label className="permissions-select-item">
                      <input
                        type="checkbox"
                        checked={allRolesSelected}
                        onChange={handleToggleSelectAllRoles}
                      />
                      <span className="permissions-select-item-label">Select all</span>
                    </label>

                    {roles.map((r) => {
                      const roleId = parseInt(r.id, 10);
                      if (Number.isNaN(roleId)) return null;
                      return (
                        <label key={r.id} className="permissions-select-item">
                          <input
                            type="checkbox"
                            checked={selectedRoleIds.includes(roleId)}
                            onChange={() => toggleRole(roleId)}
                          />
                          <span className="permissions-select-item-label">{r.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Resume */}
        <div className="form-section">
          <h2 className="section-title">Resume</h2>
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Resume</label>
              {resumeUrl ? (
                <div style={{ marginBottom: 10 }}>
                  Current:{" "}
                  <a href={resumeUrl} target="_blank" rel="noreferrer">
                    View
                  </a>
                </div>
              ) : null}
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
        </div>

        {/* Latest Followup */}
        <div className="editlead-followup-section">
          <h2 className="section-title">Latest Followup</h2>
          <div className="editlead-followup-grid">
            <div className="form-group">
              <label>Followup Date</label>
              <input
                type="datetime-local"
                name="followupDate"
                value={followupData.followupDate}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group">
              <label>Followup Type</label>
              <input
                type="text"
                name="followupType"
                value={followupData.followupType}
                onChange={handleFollowupChange}
               
              />
            </div>
            <div className="form-group">
              <label>Next Followup Date</label>
              <input
                type="datetime-local"
                name="nextFollowupDate"
                value={followupData.nextFollowupDate}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group full-width">
              <label>Remarks</label>
              <textarea
                name="remarks"
                value={followupData.remarks}
                onChange={handleFollowupChange}
                rows="3"
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-update-lead" disabled={submitting}>
            {submitting ? "Updating..." : "Update Job Lead"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default EditJobLead;

